<script setup lang="ts">
  import { computed } from "vue";
  import { NProgress, NTag, NTooltip, NSpace, NIcon } from "naive-ui";
  import { $t } from "@/locales";

  interface Props {
    currentTier: string;
    totalPaidInUnit: number;
    isMobile: boolean;
    direction: "ltr" | "rtl";
  }

  const props = defineProps<Props>();

  const tierRequirements = {
    tier0: 0,
    tier1: 0,
    tier2: 10,
    tier3: 50,
    tier4: 250,
    tier5: 1000,
  };

  const tiers = ["tier0", "tier1", "tier2", "tier3", "tier4", "tier5"];

  const currentTierIndex = computed(() => {
    return tiers.indexOf(props.currentTier);
  });

  const progressPercentage = computed(() => {
    const currentIndex = currentTierIndex.value;
    if (currentIndex === -1) return 0;
    if (currentIndex === tiers.length - 1) return 100;

    const currentRequirement =
      tierRequirements[props.currentTier as keyof typeof tierRequirements];
    const nextTier = tiers[currentIndex + 1];
    const nextRequirement =
      tierRequirements[nextTier as keyof typeof tierRequirements];

    if (props.totalPaidInUnit >= nextRequirement) {
      return ((currentIndex + 1) / (tiers.length - 1)) * 100;
    }

    if (nextRequirement <= currentRequirement) {
      return (currentIndex / (tiers.length - 1)) * 100;
    }

    const progressInCurrentTier =
      (props.totalPaidInUnit - currentRequirement) /
      (nextRequirement - currentRequirement);
    const baseProgress = currentIndex / (tiers.length - 1);
    const tierProgress =
      (1 / (tiers.length - 1)) * Math.max(0, progressInCurrentTier);

    return (baseProgress + tierProgress) * 100;
  });

  const getTierStatus = (tierIndex: number) => {
    const currentIndex = currentTierIndex.value;
    if (tierIndex < currentIndex) return "completed";
    if (tierIndex === currentIndex) return "current";
    return "upcoming";
  };

  const getTierName = (tier: string) => {
    const tierNames: Record<string, string> = {
      tier0: $t("limits.free"),
      tier1: "1",
      tier2: "2",
      tier3: "3",
      tier4: "4",
      tier5: "5",
    };
    return tierNames[tier] || tier;
  };

  const getTierTooltip = (tier: string) => {
    const requirement = tierRequirements[tier as keyof typeof tierRequirements];
    const tierName = getTierName(tier);

    if (tier === "tier0") {
      return `${$t("limits.usageTier")} ${tierName} - ${$t("limits.emailVerificationRequired")}`;
    }

    if (tier === "tier1") {
      return `${$t("limits.usageTier")} ${tierName} - ${$t("limits.phoneVerificationRequired")}`;
    }

    return `${$t("limits.usageTier")} ${tierName} - ${requirement} ${$t("billing.UNIT")}`;
  };

  const getTagType = (status: string) => {
    switch (status) {
      case "completed":
        return "success";
      case "current":
        return "info";
      case "upcoming":
        return "default";
      default:
        return "default";
    }
  };

  const getIconColor = (status: string) => {
    switch (status) {
      case "completed":
        return "#52c41a";
      case "current":
        return "#8d51ff";
      case "upcoming":
        return "#d9d9d9";
      default:
        return "#d9d9d9";
    }
  };
</script>

<template>
  <div
    :style="{ direction: direction, padding: isMobile ? '12px 0' : '16px 0' }"
  >
    <NSpace vertical :size="16">
      <!-- Progress Bar -->
      <div style="position: relative" :dir="direction">
      	<NProgress
      		:percentage="progressPercentage"
      		:show-indicator="false"
      		:height="isMobile ? 6 : 8"
      		processing
      		:style="{
      			borderRadius: '8px',
      			overflow: 'hidden',
      		}"
      	/>
        <!-- Progress percentage display -->
        <div
        	v-if="progressPercentage < 100"
        	:style="{
        		position: 'absolute',
        		top: '-24px',
        		...(direction === 'rtl'
        			? { right: `${Math.min(progressPercentage, 95)}%` }
        			: { left: `${Math.min(progressPercentage, 95)}%` }
        		),
        		transform: 'translateX(-50%)',
        		fontSize: '12px',
        		fontWeight: '600',
        		color: 'var(--n-text-color)',
        		background: 'var(--n-color)',
        		padding: '2px 6px',
        		borderRadius: '4px',
        		boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        		whiteSpace: 'nowrap',
        	}"
        >
        	{{ progressPercentage.toFixed(1) }}%
        </div>
      </div>

      <!-- Tier Progress Visualization -->
      <div v-if="!isMobile">
      <!-- Desktop: Horizontal Layout -->
      <NSpace
      :size="16"
      justify="space-between"
      align="center"
      style="width: 100%"
      >
      <div
      v-for="(tier, index) in tiers"
      :key="tier"
      style="display: flex; align-items: center; gap: 8px"
      >
      <NTooltip trigger="hover" placement="top">
      <template #trigger>
      <div
      style="
      width: 24px;
      height: 24px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 2px solid var(--n-border-color);
      transition: all 0.3s ease;
      cursor: pointer;
      "
      :style="{
      backgroundColor: getIconColor(getTierStatus(index)),
      transform:
      getTierStatus(index) === 'current'
      ? 'scale(1.1)'
      : 'scale(1)',
      }"
      >
      <NIcon
      v-if="getTierStatus(index) === 'completed'"
      size="16"
      color="white"
      >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
      <path
      fill="currentColor"
      d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"
      />
      </svg>
      </NIcon>
      <NIcon
      v-else-if="getTierStatus(index) === 'current'"
      size="16"
      color="white"
      >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
      <path
      fill="currentColor"
      d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
      />
      </svg>
      </NIcon>
      </div>
      </template>
      <span>{{ getTierTooltip(tier) }}</span>
      </NTooltip>
      
      <NTag :type="getTagType(getTierStatus(index))" size="small" round>
      {{ getTierName(tier) }}
      </NTag>
      </div>
      </NSpace>
      </div>
      
      <div v-else>
      <!-- Mobile: Vertical Layout with Enhanced Icons -->
      <div style="display: flex; flex-direction: column; gap: 16px; align-items: center;">
      <div
      v-for="(tier, index) in tiers"
      :key="tier"
      style="
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      padding: 12px 16px;
      border-radius: 12px;
      transition: all 0.3s ease;
      "
      :style="{
      backgroundColor: getTierStatus(index) === 'current'
      ? 'var(--n-color-hover)'
      : 'transparent',
      border: getTierStatus(index) === 'current'
      ? '2px solid var(--n-border-color)'
      : '1px solid var(--n-divider-color)',
      }"
      >
      <!-- Left: Icon and Tier Info -->
      <div style="display: flex; align-items: center; gap: 12px;">
      <NTooltip trigger="hover" :placement="direction === 'rtl' ? 'left' : 'right'">
      <template #trigger>
      <div
      style="
      width: 32px;
      height: 32px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 2px solid;
      transition: all 0.3s ease;
      cursor: pointer;
      position: relative;
      "
      :style="{
      backgroundColor: getIconColor(getTierStatus(index)),
      borderColor: getTierStatus(index) === 'current' ? getIconColor(getTierStatus(index)) : 'var(--n-border-color)',
      transform: getTierStatus(index) === 'current' ? 'scale(1.1)' : 'scale(1)',
      boxShadow: getTierStatus(index) === 'current' ? '0 4px 12px rgba(141, 81, 255, 0.3)' : 'none',
      }"
      >
      <!-- Completed Icon -->
      <NIcon
      v-if="getTierStatus(index) === 'completed'"
      size="18"
      color="white"
      >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
      <path
      fill="currentColor"
      d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"
      />
      </svg>
      </NIcon>
      <!-- Current Tier Icon -->
      <NIcon
      v-else-if="getTierStatus(index) === 'current'"
      size="18"
      color="white"
      >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
      <path
      fill="currentColor"
      d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
      />
      </svg>
      </NIcon>
      <!-- Upcoming Tier Icon -->
      <NIcon
      v-else
      size="16"
      :color="getIconColor(getTierStatus(index))"
      >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
      <path
      fill="currentColor"
      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
      opacity="0.5"
      />
      </svg>
      </NIcon>
      
      <!-- Pulse animation for current tier -->
      <div
      v-if="getTierStatus(index) === 'current'"
      style="
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: 50%;
      border: 2px solid var(--n-primary-color);
      animation: pulse 2s infinite;
      "
      />
      </div>
      </template>
      <span>{{ getTierTooltip(tier) }}</span>
      </NTooltip>
      
      <div style="display: flex; flex-direction: column; gap: 4px;">
      <NTag
      :type="getTagType(getTierStatus(index))"
      size="medium"
      round
      :style="{
      fontWeight: getTierStatus(index) === 'current' ? '600' : '400',
      }"
      >
      {{ $t('limits.usageTier') }} {{ getTierName(tier) }}
      </NTag>
      <div
      style="
      font-size: 12px;
      opacity: 0.7;
      font-weight: 500;
      "
      :style="{
      color: getTierStatus(index) === 'current' ? 'var(--n-primary-color)' : 'var(--n-text-color-2)',
      }"
      >
      {{ tier === 'tier0' ? $t('limits.emailVerified') : tier === 'tier1' ? $t('limits.phoneVerified') : `${tierRequirements[tier as keyof typeof tierRequirements]} ${$t('billing.UNIT')}` }}
      </div>
      </div>
      </div>
      
      <!-- Right: Status Indicator -->
      <div style="display: flex; align-items: center; gap: 8px;">
      <div
      v-if="getTierStatus(index) === 'completed'"
      style="
      display: flex;
      align-items: center;
      gap: 4px;
      color: var(--n-success-color);
      font-size: 12px;
      font-weight: 600;
      "
      >
      <NIcon size="14">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
      <path
      fill="currentColor"
      d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"
      />
      </svg>
      </NIcon>
      {{ $t('limits.completed') }}
      </div>
      <div
      v-else-if="getTierStatus(index) === 'current'"
      style="
      display: flex;
      align-items: center;
      gap: 4px;
      color: var(--n-primary-color);
      font-size: 12px;
      font-weight: 600;
      "
      >
      <NIcon size="14">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
      <path
      fill="currentColor"
      d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
      />
      </svg>
      </NIcon>
      {{ $t('limits.current') }}
      </div>
      <div
      v-else
      style="
      display: flex;
      align-items: center;
      gap: 4px;
      color: var(--n-text-color-3);
      font-size: 12px;
      "
      >
      <NIcon size="14">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
      <path
      fill="currentColor"
      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z"
      opacity="0.6"
      />
      </svg>
      </NIcon>
      {{ $t('limits.upcoming') }}
      </div>
      </div>
      </div>
      </div>
      </div>
    </NSpace>
  </div>
</template>

<style scoped>
/* RTL support for progress animation */
:deep(.n-progress .n-progress-graph .n-progress-graph-line .n-progress-graph-line-fill) {
	transition: all 0.3s ease;
}

/* Reverse animation direction for RTL */
[dir="rtl"] :deep(.n-progress .n-progress-graph .n-progress-graph-line .n-progress-graph-line-fill::after) {
	animation-direction: reverse;
}

/* Also handle the processing stripes animation for RTL */
[dir="rtl"] :deep(.n-progress.n-progress--processing .n-progress-graph .n-progress-graph-line .n-progress-graph-line-fill::after) {
	animation-direction: reverse;
}

/* Pulse animation for current tier on mobile */
@keyframes pulse {
	0% {
		transform: scale(1);
		opacity: 1;
	}
	50% {
		transform: scale(1.05);
		opacity: 0.7;
	}
	100% {
		transform: scale(1);
		opacity: 1;
	}
}

/* Mobile tier card hover effect */
@media (max-width: 768px) {
	.tier-card:hover {
		transform: translateY(-2px);
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
	}
}
</style>
